import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { avatarDetail } from '../lod.js';
import { profile } from './profile.js';

export const PERSON_LANDMARKS = Object.freeze({ headY:2.12, hipY:.92, shoulderY:1.6, shoulderX:.35,
  elbowY:-.32, kneeY:-.42, ankleY:.08, legX:.17, footZ:.06 });
export const HEAD_PROFILE = [
  { y: -.35, rx: .105, rz: .12, z: .015 },
  { y: -.30, rx: .205, rz: .225, z: .01 },
  { y: -.18, rx: .32, rz: .30 },
  { y: -.025, rx: .372, rz: .335 },
  { y: .14, rx: .378, rz: .34, z: -.008 },
  { y: .27, rx: .32, rz: .30, z: -.015 },
  { y: .37, rx: .19, rz: .19, z: -.018 },
  { y: .405, rx: .025, rz: .03, z: -.02 }
];
const merge = geos => mergeGeometries(geos.map(g => {
  const n = g.index ? g.toNonIndexed() : g;
  n.deleteAttribute('uv'); return n;
}));

export function buildAnatomy() {
  const segments = avatarDetail() === 'studio' ? 24 : 16;
  const skull = profile(HEAD_PROFILE, { segments, power: .8 });
  const skinSurface = new THREE.Mesh(skull, new THREE.MeshBasicMaterial());
  skinSurface.updateMatrixWorld(true);
  const ray = new THREE.Raycaster();
  const surface = (x, y, offset = .006) => {
    ray.set(new THREE.Vector3(x, y, 1), new THREE.Vector3(0, 0, -1));
    const hit = ray.intersectObject(skinSurface)[0];
    if (!hit) throw new Error(`Face landmark outside skull: ${x}, ${y}`);
    return [x, y, hit.point.z + offset];
  };
  const patch = (points, indices) => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(points.flat(), 3));
    geo.setIndex(indices); geo.computeVertexNormals(); return geo;
  };
  // Nose roots sit on the face; a few shared planes form bridge, tip and underside.
  const nose = patch([
    surface(-.046, -.085, .001), surface(.046, -.085, .001),
    surface(.031, .055, .001), surface(-.031, .055, .001),
    surface(0, -.05, .078)
  ], [0,1,4, 1,2,4, 2,3,4, 3,0,4]);
  const ears = [-1, 1].map(side => profile([
    {y:-.12,rx:.018,rz:.022}, {y:-.08,rx:.042,rz:.05},
    {y:.025,rx:.045,rz:.047}, {y:.06,rx:.018,rz:.024}
  ], {segments:8}).translate(side*.372,0,-.008));
  const neck = profile([
    {y:-.47,rx:.12,rz:.105,z:-.025}, {y:-.35,rx:.095,rz:.095,z:-.02},
    {y:-.27,rx:.10,rz:.10,z:-.015}
  ], {segments:10});
  const chest = profile([
    {y:1.49,rx:.282,rz:.169},{y:1.60,rx:.29,rz:.159},{y:1.70,rx:.113,rz:.098}
  ], {segments:12,power:.55}).translate(0,-PERSON_LANDMARKS.headY,0);
  const head = merge([skull, nose, ...ears, neck, chest]);

  const features = [];
  for (const side of [-1,1]) {
    const points=[surface(side*.13,.015)], indices=[];
    for(let i=0;i<12;i++){
      const a=i*Math.PI*2/12;
      points.push(surface(side*.13+Math.cos(a)*.04,.015+Math.sin(a)*.05));
      indices.push(0,i+1,(i+1)%12+1);
    }
    features.push(patch(points,indices));
  }
  const ribbon = (fn, n) => {
    const points=[],indices=[];
    for(let i=0;i<=n;i++){
      const {x,y,width}=fn(i/n);
      points.push(surface(x,y-width/2),surface(x,y+width/2));
      if(i<n){const k=i*2;indices.push(k,k+2,k+1,k+2,k+3,k+1);}
    }
    return patch(points,indices);
  };
  features.push(ribbon(t=>({x:(t-.5)*.20,y:-.16-.027*Math.sin(Math.PI*t),width:.005+.012*Math.sin(Math.PI*t)}),10));
  for(const side of [-1,1])features.push(ribbon(t=>({x:side*.13+(t-.5)*.105,y:.115+.014*Math.sin(Math.PI*t),width:.009+.006*Math.sin(Math.PI*t)}),5));
  const face=merge(features).translate(0,PERSON_LANDMARKS.headY,0);
  skinSurface.material.dispose();

  const hip = profile([
    {y:-.15,rx:.225,rz:.145}, {y:-.07,rx:.26,rz:.16}, {y:.075,rx:.245,rz:.15}, {y:.15,rx:.23,rz:.145}
  ], {segments:12,power:.55});
  const leg = profile([
    {y:-.86,rx:.083,rz:.084}, {y:-.70,rx:.091,rz:.09}, {y:-.50,rx:.108,rz:.10},
    {y:-.40,rx:.097,rz:.10}, {y:-.17,rx:.125,rz:.125}, {y:0,rx:.115,rz:.11}
  ], {segments:8,power:.75});
  const arm = () => merge([
    // Palm, wrist and forearm share rings: no capped solids separated at the
    // wrist, which used to leave a visible gap when the hand was raised.
    // Extend the tapered tip to suggest joined fingers without separate digits.
    profile([
      {y:-.805,rx:.04,rz:.039,z:.016,power:.65}, {y:-.77,rx:.062,rz:.047,z:.012,power:.65},
      {y:-.645,rx:.069,rz:.054,z:.007,power:.65},
      {y:-.59,rx:.052,rz:.055}, {y:-.47,rx:.071,rz:.072}, {y:-.32,rx:.07,rz:.074},
      {y:-.22,rx:.083,rz:.085}, {y:-.025,rx:.103,rz:.10}, {y:.035,rx:.045,rz:.045}
    ], {segments:8})
  ]);
  return {head,face,hip,leg,armLeft:arm(),armRight:arm()};
}
